import type { WatchCaseRecordsResponse } from '@compass/client/types/case_record';
import { WatchEventType } from '@compass/client/types/case_record';
import { caseRecordService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseWatchCaseRecordOptions {
  /** Enable/disable the watch connection. Defaults to true. */
  enabled?: boolean;
  /** Callback when connection status changes */
  onStatusChange?: (status: ConnectionStatus) => void;
}

interface UseWatchCaseRecordReturn {
  /** Current connection status */
  connectionStatus: ConnectionStatus;
  /** Manually reconnect the SSE connection */
  reconnect: () => void;
}

/** Maximum number of consecutive retry attempts before giving up */
const MAX_RETRIES = 10;

/** Base delay in ms for exponential backoff (doubles each retry: 1s, 2s, 4s, ...) */
const BASE_RETRY_DELAY_MS = 1000;

/** Maximum delay between retries (30 seconds) */
const MAX_RETRY_DELAY_MS = 30000;

/**
 * Calculate retry delay with exponential backoff and jitter.
 * Delay = min(base * 2^attempt, max) + random jitter (0-25% of delay)
 */
function getRetryDelay(attempt: number): number {
  const exponentialDelay = Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS);
  const jitter = exponentialDelay * 0.25 * Math.random();
  return exponentialDelay + jitter;
}

/**
 * Hook to watch a case record for real-time updates via Server-Sent Events (SSE).
 *
 * This hook establishes an SSE connection to receive live updates for a specific
 * case record. When updates are received, it automatically updates the React Query
 * cache, eliminating the need for polling.
 *
 * **Resilience features:**
 * - Automatic reconnection with exponential backoff on connection loss
 * - Tab visibility detection — reconnects when the tab becomes visible again
 * - Network online/offline detection — reconnects when the browser comes back online
 * - Invalidates the React Query cache on reconnect to catch missed updates
 *
 * @param caseId - The ID of the case record to watch. Pass null to disable watching.
 * @param options - Configuration options for the watch connection.
 * @returns Connection status and reconnect function.
 *
 * @example
 * ```tsx
 * // Basic usage - watches case and updates cache automatically
 * const { connectionStatus } = useWatchCaseRecord(caseId);
 *
 * // With options
 * const { connectionStatus, reconnect } = useWatchCaseRecord(caseId, {
 *   enabled: isDetailView,
 *   onStatusChange: (status) => console.log('Connection:', status),
 * });
 * ```
 */
export const useWatchCaseRecord = (
  caseId: string | null,
  options: UseWatchCaseRecordOptions = {},
): UseWatchCaseRecordReturn => {
  const { enabled = true, onStatusChange } = options;

  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const abortControllerRef = useRef<AbortController | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');

  // Track reconnection count to trigger effect re-run
  const [reconnectCount, setReconnectCount] = useState(0);

  // Update status and call callback
  const updateStatus = useCallback(
    (status: ConnectionStatus) => {
      setConnectionStatus(status);
      onStatusChange?.(status);
    },
    [onStatusChange],
  );

  // Manual reconnect function — resets retry counter so user always gets a fresh attempt
  const reconnect = useCallback(() => {
    // Abort existing connection if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Trigger effect re-run
    setReconnectCount(c => c + 1);
  }, []);

  useEffect(() => {
    // Skip if not enabled or missing required data
    if (!enabled || !caseId || !selectedOrganizationId || !selectedProgramId) {
      updateStatus('disconnected');
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let retryCount = 0;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    /**
     * Invalidate the case query so React Query refetches the latest data.
     * Called on reconnect to catch any updates missed while disconnected.
     */
    const invalidateCaseQuery = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.byId(caseId) });
    };

    /**
     * Schedule a reconnection attempt with exponential backoff.
     * Returns early without retrying if the controller has been aborted
     * (component unmounted or dependencies changed).
     */
    const scheduleRetry = () => {
      if (abortController.signal.aborted) return;

      if (retryCount >= MAX_RETRIES) {
        console.warn(
          `[useWatchCaseRecord] Max retries (${MAX_RETRIES}) reached for case ${caseId}`,
        );
        updateStatus('error');
        return;
      }

      const delay = getRetryDelay(retryCount);
      retryCount++;
      console.debug(
        `[useWatchCaseRecord] Reconnecting in ${Math.round(delay)}ms (attempt ${retryCount}/${MAX_RETRIES})`,
      );

      retryTimeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          startWatching();
        }
      }, delay);
    };

    /** Process a single watch event — updates React Query cache accordingly. */
    const handleWatchEvent = (event: WatchCaseRecordsResponse) => {
      if (event.event_type === WatchEventType.WATCH_EVENT_TYPE_HEARTBEAT) {
        return;
      }

      if (
        event.case_record &&
        (event.event_type === WatchEventType.WATCH_EVENT_TYPE_UPDATED ||
          event.event_type === WatchEventType.WATCH_EVENT_TYPE_INITIAL)
      ) {
        queryClient.setQueryData(queryKeys.cases.byId(caseId), event.case_record);

        // A case UPDATE often coincides with a call starting or ending (the case
        // state flips to/from a *_human_call state). The calls list and the
        // selected call's detail are otherwise only polled (≈30s / 10s), so the
        // live Human Call tab — gated on the call's IN_PROGRESS status — would
        // appear/dismiss up to a poll-interval late. Refetch those call queries
        // for THIS case now so they react to the SSE event promptly. Scoped by
        // caseId; the queries refetch only if currently mounted.
        if (event.event_type === WatchEventType.WATCH_EVENT_TYPE_UPDATED) {
          queryClient.invalidateQueries({
            predicate: query => {
              const key = query.queryKey;
              return (
                Array.isArray(key) &&
                (key[0] === 'call' || key[0] === 'caseRecordCalls') &&
                key.includes(caseId)
              );
            },
          });
        }
      }

      if (event.event_type === WatchEventType.WATCH_EVENT_TYPE_DELETED) {
        queryClient.invalidateQueries({ queryKey: queryKeys.cases.byId(caseId) });
      }
    };

    const startWatching = async () => {
      if (abortController.signal.aborted) return;

      updateStatus('connecting');

      try {
        for await (const event of caseRecordService.watchCaseRecords(
          {
            organization_id: selectedOrganizationId,
            program_id: selectedProgramId,
            case_record_ids: [caseId],
          },
          {
            signal: abortController.signal,
            onOpen: () => {
              retryCount = 0;
              updateStatus('connected');
            },
            onClose: () => updateStatus('disconnected'),
            onError: () => updateStatus('error'),
          },
        )) {
          handleWatchEvent(event);
        }

        // Stream ended normally (server closed connection) — schedule reconnect
        if (!abortController.signal.aborted) {
          updateStatus('disconnected');
          invalidateCaseQuery();
          scheduleRetry();
        }
      } catch (error) {
        // Don't log or retry abort errors - they're expected during cleanup
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('[useWatchCaseRecord] Connection error:', error);
        updateStatus('error');
        invalidateCaseQuery();
        scheduleRetry();
      }
    };

    startWatching();

    // --- Visibility change handler ---
    // When the tab becomes visible again, force a full reconnect cycle to catch
    // any missed updates. Browsers may throttle or kill background connections.
    // We abort + bump reconnectCount so the effect re-runs cleanly (cleanup
    // tears down the old connection, new effect creates a fresh one).
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (abortController.signal.aborted) return;

      console.debug('[useWatchCaseRecord] Tab became visible, reconnecting');
      abortController.abort();
      setReconnectCount(c => c + 1);
    };

    // --- Network online handler ---
    // When the browser comes back online after being offline, reconnect.
    const handleOnline = () => {
      if (abortController.signal.aborted) return;

      console.debug('[useWatchCaseRecord] Network online, reconnecting');
      abortController.abort();
      setReconnectCount(c => c + 1);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    // Cleanup: abort the connection when dependencies change or component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      clearTimeout(retryTimeoutId);
      abortController.abort();
      abortControllerRef.current = null;
    };
  }, [
    caseId,
    selectedOrganizationId,
    selectedProgramId,
    queryClient,
    enabled,
    updateStatus,
    reconnectCount,
  ]);

  return {
    connectionStatus,
    reconnect,
  };
};
